import { Maybe } from 'maybeasy';
import { AppyError } from '../../../../../Appy';
import { MissingLinkError } from '../../../../../LinkyLinky';
import {
  FormOutValues,
  FormTemplate,
  HasFormTemplates,
} from '../../../../../Resource/FormTemplates/Types';
import { Link, Resource } from '../../../../../Resource/Types';

export interface License {
  id: number;
  name: string;
  expiresOn: Date;
  activationsRemaining: Maybe<number>;
}

export type LicenseResource = Resource<License>;

export interface LicenseAllocationsFormPayload {
  allocatableLicenses: ReadonlyArray<LicenseResource>;
}

export type LicenseAllocationsFormResource = Resource<LicenseAllocationsFormPayload> &
  HasFormTemplates;

export interface SubmittedLicenseAllocationsPayload {
  allocatedLicenses: ReadonlyArray<LicenseResource>;
}

export type SubmittedLicenseAllocationsResource = Resource<SubmittedLicenseAllocationsPayload>;

interface Waiting {
  kind: 'waiting';
}

export interface Loading {
  kind: 'loading';
  link: Link;
}

interface MissingTemplateError {
  kind: 'missing-template-error';
}

export const missingTemplate = (): MissingTemplateError => ({ kind: 'missing-template-error' });

export type LoadError = AppyError | MissingTemplateError;

export interface LoadingError {
  kind: 'loading-error';
  error: LoadError;
}

export interface Ready {
  kind: 'ready';
  resource: LicenseAllocationsFormResource;
  template: FormTemplate;
}

export interface Submitting {
  kind: 'submitting';
  resource: LicenseAllocationsFormResource;
  template: FormTemplate;
  values: FormOutValues;
}

export type SubmitError = AppyError | MissingLinkError;

export interface SubmittingError {
  kind: 'submitting-error';
  resource: LicenseAllocationsFormResource;
  template: FormTemplate;
  error: SubmitError;
}

export interface Submitted {
  kind: 'submitted';
  resource: SubmittedLicenseAllocationsResource;
  template: FormTemplate;
}

export const waiting = (): Waiting => ({
  kind: 'waiting',
});

export const loading = (link: Link): Loading => ({
  kind: 'loading',
  link,
});

export const loadingError = (error: LoadError): LoadingError => ({
  kind: 'loading-error',
  error,
});

export const ready = (resource: LicenseAllocationsFormResource, template: FormTemplate): Ready => ({
  kind: 'ready',
  resource,
  template,
});

export const submitting = (
  resource: LicenseAllocationsFormResource,
  template: FormTemplate,
  values: FormOutValues,
): Submitting => ({
  kind: 'submitting',
  resource,
  template,
  values,
});

export const submittingError = (
  resource: LicenseAllocationsFormResource,
  template: FormTemplate,
  error: SubmitError,
): SubmittingError => ({
  kind: 'submitting-error',
  resource,
  template,
  error,
});

export const submitted = (
  resource: SubmittedLicenseAllocationsResource,
  template: FormTemplate,
): Submitted => ({
  kind: 'submitted',
  resource,
  template,
});

export type State =
  | Waiting
  | Loading
  | LoadingError
  | Ready
  | Submitting
  | SubmittingError
  | Submitted;

export interface SubmissionSuccess {
  kind: 'submission-success';
  resource: SubmittedLicenseAllocationsResource;
}

export interface ValidationErrors {
  kind: 'validation-errors';
  resource: LicenseAllocationsFormResource;
}

export type SubmissionResponse = SubmissionSuccess | ValidationErrors;
